import { createEntityAdapter } from '@reduxjs/toolkit'

import { checkIsPending, getLatestReaction } from './utils'

import { backendApi } from '@/stores/backendApi'

export const jobProposalsApi = backendApi.injectEndpoints({
    endpoints: (builder) => {
        return {
            listRecommendations: builder.query({
                query: (sfId) => ({
                    url: `/v2/job-seekers/${sfId}/recommendations`,
                    params: { limit: 200 },
                }),
                // see: https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#normalizing-data-with-createentityadapter
                /** @param {{ items: any[] }} response */
                transformResponse: (response) => {
                    return recommendationsAdapter.addMany(
                        recommendationsAdapter.getInitialState(),
                        response.items.filter((item) => item.bFacility.location)
                    )
                },
                // queryFn: async (_, queryApi) => {
                //     const { data } = await queryApi.dispatch(
                //         backendApi.endpoints.listFacilities.initiate({
                //             limit: 5,
                //         })
                //     )
                //     return {
                //         data: recommendationsAdapter.addMany(
                //             recommendationsAdapter.getInitialState(),
                //             data.items.map((item) => ({
                //                 aFacCode: item.code,
                //                 aFacCodes: [],
                //                 bFacCode: item.code,
                //                 score: 100,
                //                 aFacility: item,
                //                 bFacility: item,
                //             }))
                //             // []
                //         ),
                //     }
                // },
            }),
            countProposals: builder.query({
                query: (sfId) => ({
                    url: `/v1/jobseekers/${sfId}/proposals/count`,
                }),
            }),
            sendEvaluations: builder.mutation({
                query: ({ sfId, ...body }) => ({
                    url: `/v1/jobseekers/${sfId}/evaluations`,
                    method: 'POST',
                    body,
                }),
            }),

            // 提案書改善ここから
            getContactHistory: builder.query({
                query: (jobSeekerSfId) => ({
                    url: `/v2/job-seekers/${jobSeekerSfId}/proposals`,
                    params: {
                        sort: 'createdAt',
                        direction: 'desc',
                    },
                }),
                /** @param {{ items: any[] }} response */
                transformResponse: (response) => {
                    const facilities = new Set()
                    let offerTotal = 0
                    let unreadNotification = 0
                    const items = response.items.map((item) => {
                        const pages = item.pages.map((page) => {
                            // console.log(page.status, checkIsPending(page))
                            if (!checkIsPending(page)) {
                                if (page.public?.facility?.code) {
                                    facilities.add(page.public.facility.code)
                                }
                                if (page.public?.offer) {
                                    offerTotal++
                                }
                            }
                            const latestReaction = getLatestReaction(
                                page.reactions
                            )
                            if (latestReaction?.unread) {
                                unreadNotification++
                            }
                            return {
                                ...page,
                                latestReaction,
                            }
                        })
                        return {
                            ...item,
                            pages,
                        }
                    })
                    return {
                        count: items.length,
                        items,
                        unreadNotification,
                        proposed: {
                            facilityTotal: Array.from(facilities).length,
                            offerTotal,
                        },
                    }
                },
                providesTags: (result, error, jobSeekerSfId) => {
                    // console.log({ result })
                    /** @type {{ type: 'JobProposal'; id: any }[]} */
                    const tags = [
                        {
                            type: 'JobProposal',
                            id: `LIST_${jobSeekerSfId}`,
                        },
                    ]
                    result.items.forEach(({ _id }) => {
                        tags.push({
                            type: 'JobProposal',
                            id: _id,
                        })
                    })
                    return tags
                },
            }),
            getJobProposal: builder.query({
                query: (id) => ({
                    url: `/v2/proposals/${id}`,
                }),
                providesTags: (result, error, id) => [
                    { type: 'JobProposal', id },
                ],
            }),
            getProposalPage: builder.query({
                query: ({ proposalV2Id, proposalPageId }) => ({
                    url: `/v2/proposals/${proposalV2Id}/pages/${proposalPageId}`,
                }),
                // 'ProposalPage' tag を独立させる。'JobProposal' を共有すると、
                // `createJobProposal` / `patchJobProposal` 等の invalidator に
                // 巻き添えで invalidate される懸念があるため分離する。
                providesTags: (result, error, { proposalPageId }) => [
                    { type: 'ProposalPage', id: proposalPageId },
                ],
            }),
            createJobProposal: builder.mutation({
                query: (body) => ({
                    url: '/v2/proposals',
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (result, error, { jobSeekerSfId }) => [
                    { type: 'JobProposal', id: `LIST_${jobSeekerSfId}` },
                ],
            }),
            patchJobProposal: builder.mutation({
                query: (body) => ({
                    url: `/v2/proposals/${body._id}`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: (result, error, { _id }) => {
                    return [
                        { type: 'JobProposal', id: _id },
                        {
                            type: 'JobProposal',
                            id: `LIST_${result.jobSeekerSfId}`,
                        },
                    ]
                },
            }),
            createTicketFacilityFromProposal: builder.mutation({
                query: (body) => ({
                    url: '/v1/ticket-facilities',
                    method: 'POST',
                    body,
                }),
            }),
            sendReactionReadReceipt: builder.mutation({
                queryFn: async (
                    { jobSeekerSfId, userId, proposalIds },
                    api,
                    extraOptions,
                    baseQuery
                ) => {
                    let error = undefined
                    for (const proposalId of proposalIds) {
                        const result = await baseQuery({
                            url: `/v2/job-seekers/${jobSeekerSfId}/proposals/${proposalId}/pages/reactions`,
                            method: 'POST',
                            body: {
                                userId,
                                type: 'CA_check',
                            },
                        })
                        if (result.error) {
                            error = result.error
                            break
                        }
                    }
                    if (error) {
                        return { error }
                    }
                    return {
                        data: true,
                    }
                },
                invalidatesTags: (
                    result,
                    error,
                    { jobSeekerSfId, proposalIds }
                ) => {
                    return proposalIds
                        .map((proposalId) => ({
                            type: 'JobProposal',
                            id: proposalId,
                        }))
                        .concat({
                            type: 'JobProposal',
                            id: `LIST_${jobSeekerSfId}`,
                        })
                },
            }),
            // 提案書改善ここまで
        }
    },
    overrideExisting: false,
})

export const {
    useListRecommendationsQuery,
    useCountProposalsQuery,
    useSendEvaluationsMutation,
    // 提案書改善

    // useGetContactHistoryQuery,
    useLazyGetContactHistoryQuery,
    useGetJobProposalQuery,
    useLazyGetJobProposalQuery,
    useGetProposalPageQuery,
    useCreateJobProposalMutation,
    // usePatchJobProposalMutation,
    useCreateTicketFacilityFromProposalMutation,
    useSendReactionReadReceiptMutation,
} = jobProposalsApi

/**
 * @type {import('@reduxjs/toolkit/dist/query/react/buildHooks').UseQuery<
 *     import('@reduxjs/toolkit/query').QueryDefinition<
 *         string,
 *         any,
 *         any,
 *         ContactHistory
 *     >
 * >}
 */
export const useGetContactHistoryQuery =
    jobProposalsApi.useGetContactHistoryQuery

/**
 * @type {import('@reduxjs/toolkit/dist/query/react/buildHooks').UseMutation<
 *         import('@reduxjs/toolkit/query').MutationDefinition<
 *             any,
 *             any,
 *             any,
 *             any
 *         >
 *     >}
 */
export const usePatchJobProposalMutation =
    jobProposalsApi.usePatchJobProposalMutation

const recommendationsAdapter = createEntityAdapter({
    selectId: (recommendation) => recommendation.bFacility._id,
})

/**
 * @typedef {Object} ContactHistory
 * @property {number} count
 * @property {any[]} items
 * @property {number} unreadNotification
 * @property {object} proposed
 * @property {number} proposed.facilityTotal
 * @property {number} proposed.offerTotal
 */
